/**
 * @file Query-key factories for the admin SPA's TanStack queries.
 * @module @labcraft/admin/ui/hooks/query-keys
 *
 * One file owns the `['admin', <sub>, <method>, ...args]` convention
 * so the hook layer and any imperative cache reader
 * (`queryClient.setQueryData` on 401, mutation `onSuccess`
 * invalidations) agree on shape without re-deriving it.
 *
 * Two flavors per key:
 *
 *   - **Const tuples** for argument-free keys (`qkMe`). Captured as
 *     `as const` so TanStack's tuple-typed `queryKey` parameter
 *     narrows correctly.
 *   - **Factory functions** for keys that include filter args
 *     (`qkLearnersList(filter)`). The factory returns a fresh tuple
 *     per call — TanStack's structural equality on `queryKey` handles
 *     identity, so we don't memoize.
 *
 * Prefixes are intentionally short so callers can invalidate by
 * prefix in mutations (`invalidateQueries({ queryKey: ['admin',
 * 'learners'] })` matches every learners-scoped query).
 */

import type {
  LearnerListFilters,
  LearnerStatus,
  LearnerSortField,
  LearnerSortDir,
} from '../../shared/routes.ts'
import type { EmailRecipientFilters } from '../../shared/emails_routes.ts'

////////////////////////////////////////////////////////////////////////////////
// auth
////////////////////////////////////////////////////////////////////////////////

/** Identity (current admin) key. Single global query. */
export const qkMe = ['admin', 'auth', 'me'] as const

////////////////////////////////////////////////////////////////////////////////
// learners
////////////////////////////////////////////////////////////////////////////////

/**
 * Filter shape carried in the learners-list query key. `sort`/`dir` are
 * absent until the operator explicitly picks a column (so the daemon's
 * status-derived default holds); `dir` only rides with `sort`. `limit`
 * (page size) and `offset` (page N → `(N-1)*limit`) drive the numbered
 * pager. A change to any field is a fresh query key → a fresh fetch.
 */
export type LearnersListFilter = {
  status?: LearnerStatus
  q?: string
  sort?: LearnerSortField
  dir?: LearnerSortDir
  limit?: number
  offset?: number
  tags?: string[]
  tagsNot?: string[]
}

/**
 * Learners list key for one `{status, q, sort, dir}` filter. The
 * debounced search term lives HERE (in the key), not inside the queryFn
 * body — see `use_learners.ts` for why.
 */
export function qkLearnersList(filter: LearnersListFilter) {
  return ['admin', 'learners', 'list', filter] as const
}

/** Per-learner detail key. Reserved for the page-mode learner route. */
export function qkLearner(id: string) {
  return ['admin', 'learners', 'detail', id] as const
}

/**
 * One learner's tag set (#1639). Under the `['admin','learners']` prefix so
 * a learners-scoped invalidation (on attach/detach/status) refreshes it too.
 */
export function qkLearnerTags(id: string) {
  return ['admin', 'learners', 'tags', id] as const
}

/**
 * Every distinct tag across learners (#1639) — the autocomplete source for
 * the drawer + filters. Under the learners prefix for the same reason.
 */
export const qkAllTags = ['admin', 'learners', 'all-tags'] as const

////////////////////////////////////////////////////////////////////////////////
// emails
////////////////////////////////////////////////////////////////////////////////

/**
 * Authoritative recipient-count preview for one compose filter set. The
 * filter set lives in the key so a filter change is a fresh query.
 */
export function qkEmailRecipientCount(filters: EmailRecipientFilters) {
  return ['admin', 'emails', 'recipient-count', filters] as const
}

/**
 * Candidate-learner list for one compose filter set (the multi-select
 * source). Distinct from the count key so the two cache independently.
 */
export function qkEmailCandidates(filters: LearnerListFilters) {
  return ['admin', 'emails', 'candidates', filters] as const
}

/** Campaign list. Single global query — no filter args today. */
export const qkEmailCampaigns = ['admin', 'emails', 'campaigns'] as const

/** Per-campaign delivery detail. */
export function qkEmailCampaign(id: string) {
  return ['admin', 'emails', 'campaign', id] as const
}

/** Suppression (opt-out) list. Single global query. */
export const qkEmailSuppressions = ['admin', 'emails', 'suppressions'] as const

/** Query key for the unified email-history read model (#1606 Part 11). */
export const qkEmailHistory = ['admin', 'emails', 'history'] as const

/** Selectable sender identities + default Reply-To (#1616). Single global query. */
export const qkEmailSenders = ['admin', 'emails', 'senders'] as const

////////////////////////////////////////////////////////////////////////////////
// settings
////////////////////////////////////////////////////////////////////////////////

/** Per-daemon registry of declared keys. */
export const qkSettingsSchema = ['admin', 'settings', 'schema'] as const

/** Current value of every declared key. */
export const qkSettingsValues = ['admin', 'settings', 'values'] as const

/** Full history of one daemon-scoped key, newest first. */
export function qkSettingHistory(daemon: string, key: string) {
  return ['admin', 'settings', 'history', daemon, key] as const
}

////////////////////////////////////////////////////////////////////////////////
// secrets
////////////////////////////////////////////////////////////////////////////////

/** Per-secret declarations + rotation metadata. */
export const qkSecretsList = ['admin', 'secrets', 'list'] as const

////////////////////////////////////////////////////////////////////////////////
// courses
////////////////////////////////////////////////////////////////////////////////

/** Installed-courses catalog. Single global query — no filter args today. */
export const qkCoursesList = ['admin', 'courses', 'list'] as const

////////////////////////////////////////////////////////////////////////////////
// recalibration
////////////////////////////////////////////////////////////////////////////////

/**
 * Mentor-capture recalibration signal. Single global query — no filter
 * args. The draft + content-PR flows are mutations (no cache key);
 * their results live in component state, not the query cache.
 */
export const qkRecalibrationSignal = [
  'admin',
  'recalibration',
  'signal',
] as const

////////////////////////////////////////////////////////////////////////////////
// hypervisor
////////////////////////////////////////////////////////////////////////////////

/** Hypervisor machine list (admin-gated proxy). Single global query. */
export const qkHypervisorMachines = ['admin', 'hypervisor', 'machines'] as const
